import { demoFunc } from './dees-harness-question-card.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles } from '../harness.styles.js';
import type { IHarnessQuestionRequest, IHarnessQuestionViewState } from '../interfaces.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-question-card': DeesHarnessQuestionCard;
  }
}

/**
 * A question the agent asks mid-run: option buttons (single-select answers
 * immediately, multi-select collects then confirms) plus an optional
 * free-text answer. Emits `harness-question-response` (bubbles, composed).
 * Answered cards persist showing the chosen answer(s) — the host sets
 * `response`/`respondedAt` on the request and keeps it in the array.
 */
@customElement('dees-harness-question-card')
export class DeesHarnessQuestionCard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor request: IHarnessQuestionRequest = { id: '', question: '' };

  @property({ type: Boolean })
  accessor busy: boolean = false;

  @property({ attribute: false })
  accessor viewState: IHarnessQuestionViewState | undefined = undefined;

  /** local optimistic answer; request.response (host echo) takes precedence */
  @state()
  accessor answered: string[] | undefined = undefined;

  @state()
  accessor selected: ReadonlySet<string> = new Set<string>();

  @state()
  accessor customText: string = '';

  public get effectiveResponse(): string[] | undefined {
    return this.request.response ?? this.answered ?? this.viewState?.answered;
  }

  private get effectiveSelected(): ReadonlySet<string> {
    return this.selected.size > 0
      ? this.selected
      : new Set(this.viewState?.selected ?? []);
  }

  private get effectiveCustomText(): string {
    return this.customText || this.viewState?.customText || '';
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        overflow: clip;
      }

      .card {
        padding: 16px;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-secondary);
      }

      .titleRow {
        display: flex;
        align-items: flex-start;
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-sm);
      }

      .titleRow dees-icon {
        flex: 0 0 auto;
        padding: 5px;
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-nav-selected);
        color: var(--dees-color-accent-primary);
      }

      .questionText {
        min-width: 0;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size, 13px);
        font-weight: 600;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }

      .options {
        display: block;
      }

      .options > * + * {
        margin-top: var(--dees-spacing-xs);
      }

      .option {
        width: 100%;
        display: block;
        min-height: 40px;
        padding: 8px 12px;
        border: 1px solid var(--dees-color-control-border);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
        color: var(--dees-color-text-primary);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
        text-align: left;
        transition:
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .option:hover:not(:disabled) {
        border-color: var(--dees-color-accent-primary);
      }

      .option.selected {
        border-color: var(--dees-color-accent-primary);
        background: var(--dees-color-nav-selected);
      }

      .option:disabled {
        cursor: default;
        opacity: 0.6;
      }

      .optionLabel {
        font-weight: 500;
      }

      .optionDescription {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .customRow {
        display: flex;
        gap: var(--dees-spacing-xs);
        margin-top: var(--dees-spacing-sm);
      }

      .customRow input {
        flex: 1;
        min-width: 0;
        height: var(--dees-control-height-md);
        padding: 0 var(--dees-spacing-sm);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
        outline: none;
      }

      .customRow input:focus {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .confirmRow {
        display: flex;
        justify-content: flex-end;
        margin-top: var(--dees-spacing-sm);
      }

      .answeredNote {
        display: flex;
        align-items: baseline;
        gap: var(--dees-spacing-sm);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .answeredNote .answerText {
        color: var(--dees-color-text-primary);
        font-weight: 500;
        overflow-wrap: anywhere;
      }
    `,
  ];

  public render(): TemplateResult {
    const request = this.request;
    const response = this.effectiveResponse;
    const disabled = this.busy || response !== undefined;
    return html`
      <div class="card">
        <div class="titleRow">
          <dees-icon icon="lucide:MessageCircleQuestionMark" iconSize="15"></dees-icon>
          <span class="questionText">${request.question}</span>
        </div>
        ${response
          ? html`
              <div class="answeredNote">
                Answered:
                <span class="answerText">${response.join(', ') || '—'}</span>
              </div>
            `
          : html`
              ${request.options?.length
                ? html`
                    <div class="options" role=${request.multiSelect ? 'group' : 'radiogroup'}>
                      ${request.options.map((option) => html`
                        <button
                          class="option ${this.effectiveSelected.has(option.label) ? 'selected' : ''}"
                          type="button"
                          ?disabled=${disabled}
                          @click=${() => this.handleOptionClick(option.label)}
                        >
                          <div class="optionLabel">${option.label}</div>
                          ${option.description
                            ? html`<div class="optionDescription">${option.description}</div>`
                            : ''}
                        </button>
                      `)}
                    </div>
                  `
                : ''}
              ${request.allowCustom !== false
                ? html`
                    <div class="customRow">
                      <input
                        placeholder="Answer in your own words…"
                        .value=${this.effectiveCustomText}
                        ?disabled=${disabled}
                        @input=${(event: Event) => {
                          this.customText = (event.currentTarget as HTMLInputElement).value;
                          if (this.viewState) this.viewState.customText = this.customText;
                        }}
                        @keydown=${(event: KeyboardEvent) => {
                          if (event.key === 'Enter' && this.effectiveCustomText.trim()) {
                            this.respond([...this.effectiveSelected, this.effectiveCustomText.trim()]);
                          }
                        }}
                      />
                      <dees-button
                        size="sm"
                        ?disabled=${disabled || (!this.effectiveCustomText.trim() && !this.effectiveSelected.size)}
                        @clicked=${() => {
                          const answers = [...this.effectiveSelected];
                          if (this.effectiveCustomText.trim()) answers.push(this.effectiveCustomText.trim());
                          this.respond(answers);
                        }}
                      >Send Answer</dees-button>
                    </div>
                  `
                : ''}
              ${request.multiSelect && request.allowCustom === false
                ? html`
                    <div class="confirmRow">
                      <dees-button
                        type="accent"
                        size="sm"
                        ?disabled=${disabled || !this.effectiveSelected.size}
                        @clicked=${() => this.respond([...this.effectiveSelected])}
                      >Confirm</dees-button>
                    </div>
                  `
                : ''}
            `}
      </div>
    `;
  }

  private handleOptionClick(label: string): void {
    if (this.busy || this.effectiveResponse !== undefined) return;
    if (this.request.multiSelect) {
      const next = new Set(this.effectiveSelected);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      this.selected = next;
      if (this.viewState) this.viewState.selected = [...next];
      return;
    }
    this.respond([label]);
  }

  private respond(answers: string[]): void {
    if (this.busy || this.effectiveResponse !== undefined || !answers.length) return;
    this.answered = answers;
    if (this.viewState) this.viewState.answered = answers;
    this.dispatchEvent(
      new CustomEvent('harness-question-response', {
        detail: {
          requestId: this.request.id,
          answers,
          request: this.request,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
}
